import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { userServices } from "../../services/UserServices";
import { toast } from "react-toastify";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchUsers = async (page = 1, limit = perPage, searchText = "") => {
    setLoading(true);
    try {
      const response = await userServices.fetchUsers({ page, limit, search: searchText });

      if (response.status === 200 && response.data.success) {
        const data = response.data.data;
        setUsers(data.users || []);
        setTotalRows(data.total || 0);
        setCurrentPage(data.page || 1);
      } else {
        toast.error(response.data?.message || "Failed to fetch users.");
      }
    } catch (error) {
      toast.error(error.message || "Error fetching users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handlePageChange = (page) => {
    fetchUsers(page, perPage, search);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    fetchUsers(page, newPerPage, search);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    fetchUsers(1, perPage, value); // reset to page 1 on new search
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete this user?");
    if (confirmed) {
      try {
        await userServices.deleteUser(id);
        fetchUsers(currentPage, perPage, search);
      } catch (error) {
        toast.error("Failed to delete user.");
      }
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.fullName,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phoneNumber || "—",
      sortable: true,
    },
    {
      name: "KYC Status",
      selector: (row) => row.kyc_status || "pending",
      sortable: true,
      cell: (row) => {
        const status = row.kyc_status || "pending";
        let badgeClass = "bg-secondary";
        if (status === "completed") badgeClass = "bg-success";
        else if (status === "in_progress") badgeClass = "bg-warning text-dark";
        else if (status === "rejected") badgeClass = "bg-danger";
        else if (status === "pending") badgeClass = "bg-secondary";

        return (
          <span className={`badge ${badgeClass}`} style={{ textTransform: "capitalize" }}>
            {status.replace("_", " ")}
          </span>
        );
      }
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="btn-group" role="group">
          <Link
            to={`/admin/users/edit/${row._id}`}
            type="button"
            className="btn btn-outline-secondary"
          >
            <i className="icofont-edit text-success"></i>
          </Link>
          <Link
            to={`/admin/transactions/user/${row._id}`}
            type="button"
            className="btn btn-outline-secondary"
          >
            <i className="icofont-random text-info"></i>
          </Link>
          <Link
            to={`/admin/user/wallet/${row._id}`}
            type="button"
            className="btn btn-outline-secondary"
          >
            <i className="icofont-wallet text-success"></i>
          </Link>
          <button
            onClick={() => handleDelete(row._id)}
            type="button"
            className="btn btn-outline-secondary deleterow"
          >
            <i className="icofont-ui-delete text-danger"></i>
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="body d-flex py-3">
      <div className="container-xxl">
        <div className="row align-items-center">
          <div className="border-0 mb-4">
            <div className="card-header py-3 no-bg bg-transparent d-flex align-items-center px-0 justify-content-between border-bottom flex-wrap">
              <h3 className="fw-bold mb-0">Users List</h3>
            </div>
          </div>
        </div>
        <div className="row align-item-center">
          <div className="col-md-12">
            <div className="card mb-3">
              <div className="card-header py-3 bg-transparent border-bottom-0">
                <div className="card-header py-3 no-bg bg-transparent d-flex align-items-center px-0 justify-content-between border-bottom flex-wrap">
                  <h6 className="mb-0 fw-bold">Users</h6>
                  <Link to="/admin/users/create" className="btn btn-primary step-btn">
                    + Add New
                  </Link>
                </div>
              </div>
              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={users}
                  progressPending={loading}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  highlightOnHover
                  defaultSortFieldId={1}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserList;
